import { type ClipboardEvent, useRef } from 'react';
import { type Editor, useEditor } from '@tiptap/react';
import { default as Document } from '@tiptap/extension-document';
import { default as Paragraph } from '@tiptap/extension-paragraph';
import { Placeholder } from '@tiptap/extensions/placeholder';
import { Dropcursor } from '@tiptap/extensions/drop-cursor';
import { CharacterCount } from '@tiptap/extensions/character-count';
import { default as Text } from '@tiptap/extension-text';
import { color } from '@/colors';
import { CSS_CLASSES } from '../constants';
import { buildEditorContent, CreateTagNode, serializeClipboardText } from '../tiptapEditorUtils';
import { useTiptapEditorHelpers } from './useTiptapEditorHelpers';
import { useEditorUpdatesExternally } from './useEditorUpdatesExternally';
import type { AvailableInput } from '../types';
import type { PromptDisplayMode } from 'web';

interface UseTiptapEditorProps {
  value: string;
  availableInputs: AvailableInput[];
  hasEditingPermissions: boolean;
  placeholder: string;
  onChange: (prompt: string) => void;
  onPaste: (evt: ClipboardEvent<HTMLDivElement>) => void;
  displayMode: PromptDisplayMode;
  maxLength: number;
  setHasFocus: (focused: boolean) => void;
  onCreatePrompt: (queryText: string) => AvailableInput | undefined;
}

export const useTiptapEditor = ({
  value,
  availableInputs,
  hasEditingPermissions,
  placeholder,
  onChange,
  onPaste,
  displayMode,
  maxLength,
  setHasFocus,
  onCreatePrompt,
}: UseTiptapEditorProps) => {
  const editorRef = useRef<Editor | null>(null);
  const lastValueRef = useRef(value);
  // Build initial content from value string (computed once on mount)
  const initialContentRef = useRef({
    type: 'doc',
    content: buildEditorContent(value, availableInputs, displayMode),
  });

  const {
    contextMenu,
    handleSetContextMenu,
    handleEditorKeyDown,
    handleEditorPaste,
    handleEditorDOMKeyDown,
    handleEditorContextMenu,
    handleEditorSelectionUpdate,
    handleEditorUpdate,
  } = useTiptapEditorHelpers({
    editorRef,
    lastValueRef,
    availableInputs,
    displayMode,
    hasEditingPermissions,
    onChange,
    onPaste,
    onCreatePrompt,
  });

  const editor = useEditor({
    onCreate: ({ editor }) => {
      editorRef.current = editor;
    },
    onDestroy: () => {
      editorRef.current = null;
    },
    extensions: [
      Document.extend({
        content: 'paragraph+',
      }),
      Paragraph,
      Text,
      CreateTagNode(),
      Dropcursor.configure({
        color: color.DataType_Text,
        width: 2,
      }),
      Placeholder.configure({
        placeholder,
      }),
      CharacterCount.configure({
        limit: maxLength,
      }),
    ],
    content: initialContentRef.current,
    editable: hasEditingPermissions,
    editorProps: {
      attributes: {
        class: CSS_CLASSES.EDITOR,
      },
      clipboardTextSerializer: (slice) => {
        return serializeClipboardText(slice);
      },
      handleKeyDown: (_view, event) => {
        return handleEditorKeyDown(event);
      },
      handlePaste: (_view, event) => {
        return handleEditorPaste(event);
      },
      handleDOMEvents: {
        keydown: (_view, event) => {
          return handleEditorDOMKeyDown(event);
        },
        contextmenu: (view, event) => {
          return handleEditorContextMenu(view, event);
        },
      },
    },
    onUpdate: ({ editor }) => {
      handleEditorUpdate(editor);
    },
    onSelectionUpdate: ({ editor }) => {
      handleEditorSelectionUpdate(editor);
    },
    onFocus: () => {
      setHasFocus(true);
    },
    onBlur: ({ editor }) => {
      setHasFocus(false);
      // Clear selection so selected tags lose their highlight when editor loses focus
      editor.commands.setTextSelection(editor.state.selection.anchor);
    },
  });

  // Handle external updates (value, displayMode, availableInputs changes)
  useEditorUpdatesExternally({
    editor,
    value,
    availableInputs,
    displayMode,
    hasEditingPermissions,
    lastValueRef,
  });

  return {
    editor,
    contextMenu,
    setContextMenu: handleSetContextMenu,
  };
};
